import {
  BLUE_YONDER_PROVIDER,
  type DropoffProviderType,
  dropoffProviderType,
  HAPPY_RETURNS_PROVIDER,
  RETURN_BEAR_PROVIDER,
  VIRTUAL_PROVIDER,
} from "@redotech/redo-return-dropoff-provider-common/dropoff-provider";
import {
  VirtualMiddleMileCarrier,
  virtualMiddleMileCarrierOf,
} from "@redotech/redo-return-dropoff-provider-common/virtual-middle-mile";
import { assertNever } from "@redotech/util/type";
import { WebhookEventType } from "../integrations/returns/blueyonder/blueyonder";
import { UniUniShipmentStatus } from "../integrations/returns/uni-uni/types";
import {
  productMatchesShipmentGroup,
  type ProductShipmentGroupFields,
} from "../shipping/shipment-group";
import {
  BlueYonderQrCodeMetadataSchema,
  type HappyReturnsQrCodeMetadata,
  HappyReturnsQrCodeMetadataSchema,
  VirtualMiddleMileTagsSchema,
  VirtualQrCodeMetadataSchema,
} from "./dropoff-types";

interface DropoffQrCodeEntry {
  provider?: DropoffProviderType | string | null;
  shipmentGroupId?: string | null;
  qrCode?: string | null;
  qrCodeUrl?: string | null;
  trackingNumber?: string | null;
  providerMetadata?: unknown;
  dropoffFlatRateFee?: number | null;
}

export interface ReturnWithDropoffs {
  dropoffs?: {
    chosen?: boolean | null;
    qrCodes: DropoffQrCodeEntry[] | null;
  } | null;
}

export function isDropoffChosen(return_: ReturnWithDropoffs): boolean {
  return !!return_.dropoffs?.chosen;
}

export function getPendingDropoffQrCodes<
  T extends { qrCodeUrl?: string | null },
>(return_: {
  dropoffs?: { chosen?: boolean | null; qrCodes: T[] | null } | null;
}): T[] {
  return (return_.dropoffs?.qrCodes ?? []).filter((entry) => !entry.qrCodeUrl);
}

export function hasPendingDropoffQrCodes(return_: ReturnWithDropoffs): boolean {
  return (
    !!return_.dropoffs?.chosen &&
    (return_.dropoffs.qrCodes ?? []).some((entry) => !entry.qrCodeUrl)
  );
}

export function getDropoffQrCodeUris(return_: ReturnWithDropoffs): string[] {
  if (!isDropoffChosen(return_)) {
    return [];
  }
  return (return_.dropoffs?.qrCodes ?? [])
    .map((qr) => qr.qrCodeUrl)
    .filter((url): url is string => !!url);
}

/**
 * Whether a provider's stored `qrCodeUrl` is a QR image the customer can have
 * scanned. Adding a provider will not compile until it answers this.
 *
 * Happy Returns is true: three of its four create_return shapes return a
 * `qr_code` (Return Bar, retailer store, and mail-no-box). The fourth,
 * mail-with-label, has no QR at all and stores its `label_url` here instead --
 * that one is a label document, so the caller's media-type check keeps it out
 * of a text message and the customer gets the link as before.
 */
const dropoffQrCodeUrlIsScannable: Record<DropoffProviderType, boolean> = {
  VIRTUAL: true,
  BLUE_YONDER: true,
  HAPPY_RETURNS: true,
  /** @deprecated ReturnBear is retired; nothing writes these entries any more. */
  RETURN_BEAR: false,
};

const scannableDropoffProviders = new Set<string>(
  dropoffProviderType.filter(
    (provider) => dropoffQrCodeUrlIsScannable[provider],
  ),
);

/** Every drop-off QR image on the return, across providers. */
export function getScannableDropoffQrCodeUris(
  return_: ReturnWithDropoffs,
): string[] {
  if (!isDropoffChosen(return_)) {
    return [];
  }
  return (return_.dropoffs?.qrCodes ?? [])
    .filter((qr) => !!qr.provider && scannableDropoffProviders.has(qr.provider))
    .map((qr) => qr.qrCodeUrl)
    .filter((url): url is string => !!url);
}

export function hasCompletedDropoffQrCodes(
  return_: ReturnWithDropoffs,
): boolean {
  const qrCodes = return_.dropoffs?.qrCodes ?? [];
  return (
    !!return_.dropoffs?.chosen &&
    qrCodes.length > 0 &&
    qrCodes.every((entry) => !!entry.qrCodeUrl)
  );
}

/**
 * Finds a dropoff QR code entry by provider and optionally shipmentGroupId.
 */
export function getDropoffQrCodeByProvider(
  return_: ReturnWithDropoffs,
  provider: DropoffProviderType,
  shipmentGroupId?: string,
): DropoffQrCodeEntry | undefined {
  return (return_.dropoffs?.qrCodes ?? []).find(
    (qr) =>
      qr.provider === provider &&
      (!shipmentGroupId || qr.shipmentGroupId === shipmentGroupId),
  );
}

/**
 * Checks if a dropoff QR code exists for the given provider (completed with a URL).
 */
export function hasDropoffQrCodeUrlForProvider(
  return_: ReturnWithDropoffs,
  provider: DropoffProviderType,
): boolean {
  return !!getDropoffQrCodeByProvider(return_, provider)?.qrCodeUrl;
}

/**
 * Gets the QR code URL for a specific provider, optionally filtered by shipmentGroupId.
 */
export function getDropoffQrCodeUrlForProvider(
  return_: ReturnWithDropoffs,
  provider: DropoffProviderType,
  shipmentGroupId?: string,
): string | undefined {
  return (
    getDropoffQrCodeByProvider(return_, provider, shipmentGroupId)?.qrCodeUrl ??
    undefined
  );
}

export type HappyReturnsAudit = NonNullable<
  HappyReturnsQrCodeMetadata["audit"]
>;

export function getFailedHappyReturnsAudits(
  return_: ReturnWithDropoffs,
): HappyReturnsAudit[] {
  return (return_.dropoffs?.qrCodes ?? []).flatMap((entry) => {
    if (entry.provider !== HAPPY_RETURNS_PROVIDER) {
      return [];
    }
    const audit = HappyReturnsQrCodeMetadataSchema.safeParse(
      entry.providerMetadata,
    ).data?.audit;
    return audit?.event === "failed_audit" ? [audit] : [];
  });
}

/**
 * Gets the provider-specific identifier for a dropoff entry.
 * - VIRTUAL: trackingNumber from the qrCode entry
 * - BLUE_YONDER: returnAuthorizationNumber from providerMetadata
 * - RETURN_BEAR: undefined
 */
export function getDropoffIdentifierForProvider(
  return_: ReturnWithDropoffs,
  provider: DropoffProviderType,
  shipmentGroupId?: string,
): string | undefined {
  const entry = getDropoffQrCodeByProvider(return_, provider, shipmentGroupId);
  return entry && dropoffIdentifierForEntry(entry, provider);
}

function dropoffIdentifierForEntry(
  entry: DropoffQrCodeEntry,
  provider: DropoffProviderType,
): string | undefined {
  switch (provider) {
    case VIRTUAL_PROVIDER:
      return entry.trackingNumber ?? undefined;
    case BLUE_YONDER_PROVIDER: {
      const parsed = BlueYonderQrCodeMetadataSchema.safeParse(
        entry.providerMetadata,
      );
      return parsed.data?.returnAuthorizationNumber || undefined;
    }
    case RETURN_BEAR_PROVIDER:
      return undefined;
    case HAPPY_RETURNS_PROVIDER: {
      const parsed = HappyReturnsQrCodeMetadataSchema.safeParse(
        entry.providerMetadata,
      );
      return parsed.data?.rmaId;
    }
    default:
      assertNever(provider);
  }
}

/**
 * The barcode printed on the package and scanned at the warehouse. Differs from
 * {@link dropoffIdentifierForEntry} for BlueYonder, whose scanned `labelValue`
 * is not the RA number.
 */
const dropoffDisplayIdentifiers: Record<
  DropoffProviderType,
  (entry: DropoffQrCodeEntry) => string | undefined
> = {
  VIRTUAL: (entry) => entry.trackingNumber ?? undefined,
  BLUE_YONDER: (entry) =>
    BlueYonderQrCodeMetadataSchema.safeParse(entry.providerMetadata).data
      ?.labelValue || undefined,
  RETURN_BEAR: () => undefined,
  HAPPY_RETURNS: (entry) =>
    HappyReturnsQrCodeMetadataSchema.safeParse(entry.providerMetadata).data
      ?.rmaId || undefined,
};

export function getDropoffDisplayIdentifier(
  entry: DropoffQrCodeEntry,
  provider: DropoffProviderType,
): string | undefined {
  return dropoffDisplayIdentifiers[provider](entry);
}

/** Scoped to the product's group so a split return reports each line's own dropoff. */
export function getDropoffIdentifierForProduct(
  return_: ReturnWithDropoffs,
  provider: DropoffProviderType,
  product: ProductShipmentGroupFields,
): string | undefined {
  const entry = (return_.dropoffs?.qrCodes ?? []).find(
    (qr) =>
      qr.provider === provider &&
      productMatchesShipmentGroup(product, qr.shipmentGroupId),
  );
  return entry && dropoffIdentifierForEntry(entry, provider);
}

/**
 * The middle-mile carrier of a Virtual entry: UniUni when none was recorded,
 * undefined when the recorded one is not a carrier this code knows.
 */
export function getVirtualMiddleMileCarrier(
  providerMetadata: unknown,
): VirtualMiddleMileCarrier | undefined {
  const parsed = VirtualMiddleMileTagsSchema.safeParse(providerMetadata ?? {});
  return parsed.success ? virtualMiddleMileCarrierOf(parsed.data) : undefined;
}

// UniUni statuses that indicate the customer has handed off the package.
// Any of these means the dropoff is past the "needs reminder" stage.
const VIRTUAL_COMPLETED_STATUSES = new Set<UniUniShipmentStatus>([
  UniUniShipmentStatus.PICKED_UP,
  UniUniShipmentStatus.PARTNER_RECEIVED,
  UniUniShipmentStatus.RECEIVED,
  UniUniShipmentStatus.GATEWAY_TRANSIT_OUT,
  UniUniShipmentStatus.GATEWAY_TO_GATEWAY_TRANSIT,
  UniUniShipmentStatus.GATEWAY_TRANSIT,
  UniUniShipmentStatus.PARCEL_SCANNED,
  UniUniShipmentStatus.OUT_FOR_DELIVERY,
  UniUniShipmentStatus.IN_TRANSIT,
  UniUniShipmentStatus.DELIVERED,
  UniUniShipmentStatus.TRANSSHIPMENT_COMPLETE,
]);

export function isDropoffCompletedForProvider(
  provider: DropoffProviderType,
  providerMetadata: unknown,
): boolean {
  switch (provider) {
    case BLUE_YONDER_PROVIDER: {
      const parsed = BlueYonderQrCodeMetadataSchema.safeParse(providerMetadata);
      if (!parsed.success) {
        return false;
      }
      return !!parsed.data.webhookEvents?.some(
        (event) => event.eventType === WebhookEventType.DROPPED_OFF,
      );
    }
    case VIRTUAL_PROVIDER: {
      const parsed = VirtualQrCodeMetadataSchema.safeParse(providerMetadata);
      if (!parsed.success) {
        return false;
      }
      const carrier = virtualMiddleMileCarrierOf(parsed.data);
      switch (carrier) {
        case VirtualMiddleMileCarrier.UNIUNI:
          return !!parsed.data.webhookEvents?.some((event) =>
            VIRTUAL_COMPLETED_STATUSES.has(event.status),
          );
        case VirtualMiddleMileCarrier.RETURN_DROP:
          // Return Drop has no webhook; nothing records its drop-off yet.
          return false;
        default:
          return assertNever(carrier);
      }
    }
    case RETURN_BEAR_PROVIDER:
      // RETURN_BEAR's dropoff QR metadata schema is empty; completion is
      // tracked via the ReturnBear case (external integration), not the QR
      // code. Returning false keeps the reminder cadence as-is for those
      // returns until a ReturnBear-specific signal is wired in.
      return false;
    case HAPPY_RETURNS_PROVIDER:
      // Happy Returns tracks completion through its own RMA, not the QR
      // metadata, so there is no stored signal to read here yet.
      return false;
    default:
      assertNever(provider);
  }
}
